// apps/web/src/components/brands/BrandManager.tsx
// Brand management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Brand } from '../../types/brand';
import { apiClient } from '../../lib/apiClient';

export function BrandManager() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/brands');
        setBrands(response.data.brands);
      } catch (err) {
        setError('Failed to load brands');
        console.error('Error loading brands:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const handleCreateBrand = async (brandData: any) => {
    try {
      const response = await apiClient.post('/api/v1/brands', brandData);
      setBrands([response.data, ...brands]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create brand');
      console.error('Error creating brand:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading brands...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="brand-manager">
      <div className="header">
        <h2>Brand Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Brand'}
        </button>
      </div>

      {showCreateForm && (
        <BrandForm 
          onSubmit={handleCreateBrand} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="brands-grid">
        {brands.map(brand => (
          <BrandCard key={brand.id} brand={brand} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function BrandCard({ brand }: { brand: Brand }) {
  return (
    <div className="brand-card">
      <div className="brand-header">
        <h3>{brand.name}</h3>
        <span className="brand-status">{brand.status}</span>
      </div>
      <p>{brand.description || 'No description'}</p>
      <p><strong>Created:</strong> {new Date(brand.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function BrandForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    website: '',
    logoUrl: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="brand-form">
      <h3>Create New Brand</h3>
      <input
        type="text"
        placeholder="Brand Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <input
        type="text"
        placeholder="Industry"
        value={formData.industry}
        onChange={(e) => setFormData({...formData, industry: e.target.value})}
      />
      <input
        type="url"
        placeholder="Website"
        value={formData.website}
        onChange={(e) => setFormData({...formData, website: e.target.value})}
      />
      <input
        type="url"
        placeholder="Logo URL"
        value={formData.logoUrl}
        onChange={(e) => setFormData({...formData, logoUrl: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Brand</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}